import { useEffect, useState } from "react";
import { Dialog } from "@mui/material";
import Button from "./DefaultButton";
import LabelComponent from "./LabelComponent";
import SelectComponent from "./SelectComponent";
import InputComponent from "./InputComponent";
import { ReactComponent as CrossCancelIcon } from "../../assets/images/crossIconAddTaskPodModal.svg";
import { ReactComponent as FullView } from "../../assets/images/icons/FullView.svg";
import { ReactComponent as ExtenedView } from "../../assets/images/icons/ExtendeView.svg";
import moment from "moment";
import ToggleMultiSwitch from "./ToggleMultiSwitch";
import { ReactComponent as EditPencil } from "../../assets/images/icons/EditPencil.svg";
import { getTitleCase } from "../../utils/utilityFunctions/stringUtility";

const CheckTypeAndReturnHtmlInput = ({ id, name, metaType, type, placeholder, value, onChange, options }) => {
  const [inputTypeData, setInputTypeData] = useState(value);

  if (type === "text") {
    if (metaType === "Hyperlink") {
      return (
        <span>
          <LabelComponent htmlFor={id || name} labelText={name} size="small" />
          <InputComponent
            id={id || name}
            type="text"
            placeholder={placeholder || `Enter Name`}
            value={inputTypeData?.textForUrl}
            onChange={(event) => {
              setInputTypeData({ ...inputTypeData, textForUrl: event?.target?.value });
              onChange({ ...inputTypeData, textForUrl: event?.target?.value });
            }}
          />
          <div className="block pb-[3px]"></div>
          <InputComponent
            id={id || name}
            type="text"
            placeholder={placeholder || `Enter URL`}
            value={inputTypeData?.url}
            onChange={(event) => {
              setInputTypeData({ ...inputTypeData, url: event?.target?.value });
              onChange({ ...inputTypeData, url: event?.target?.value });
            }}
          />
        </span>
      );
    } else {
      return (
        <span>
          <LabelComponent htmlFor={id || name} labelText={name} size="small" />
          <InputComponent
            id={id || name}
            type={type}
            placeholder={placeholder || `Enter ${name}`}
            value={inputTypeData}
            onChange={(event) => {
              setInputTypeData(event?.target?.value);
              onChange(event?.target?.value);
            }}
            maxLength={metaType?.toLowerCase() === "short text" ? 30 : 200}
          />
        </span>
      );
    }
  } else if (type === "number") {
    return (
      <span>
        <LabelComponent htmlFor={id || name} labelText={name} size="small" />
        <InputComponent
          id={id || name}
          type={type}
          placeholder={placeholder}
          value={inputTypeData}
          onChange={(event) => {
            setInputTypeData(event?.target?.value);
            onChange(event?.target?.value);
          }}
        />
      </span>
    );
  } else if (type === "date") {
    if (metaType === "DateTime") {
      const DD_MM_YYYY_DATE_FORMAT = moment(inputTypeData).format("YYYY-MM-DDTHH:mm");
      return (
        <span>
          <LabelComponent htmlFor={id || name} labelText={name} size="small" />
          <InputComponent
            id={id || name}
            type="datetime-local"
            placeholder={placeholder || `Enter ${name}`}
            value={DD_MM_YYYY_DATE_FORMAT}
            onChange={(event) => {
              const value = moment(event?.target?.value).format("DD MMM YYYY HH:mm");
              setInputTypeData(value);
              onChange(value);
            }}
          />
        </span>
      );
    } else {
      const YYYY_MM_DD_DATE_FORMAT = moment(inputTypeData).format("YYYY-MM-DD");
      return (
        <span>
          <LabelComponent htmlFor={id || name} labelText={name} size="small" />
          <InputComponent
            id={id || name}
            type="date"
            placeholder={placeholder || `Enter ${name}`}
            value={YYYY_MM_DD_DATE_FORMAT}
            onChange={(event) => {
              const value = moment(event?.target?.value).format("DD MMM YYYY");
              setInputTypeData(value);
              onChange(value);
            }}
          />
        </span>
      );
    }
  } else if (type === "dropdown") {
    return (
      <div className="h-full w-full">
        <LabelComponent htmlFor={id || name} labelText={name} size="small" />
        <SelectComponent
          size="large"
          id={id || name}
          label={name}
          options={options}
          value={value}
          onChange={(value) => {
            onChange([{ ...value?.[0], color_code: value?.[0]?.additionFields?.color_code }]);
          }}
          noFormInput={true}
        />
      </div>
    );
  } else if (type === "multiselect") {
    return (
      <span>
        <LabelComponent htmlFor={id || name} labelText={name} size="small" />
        <SelectComponent
          id={id || name}
          label={name}
          options={options}
          value={value}
          style={{ height: "36px" }}
          onChange={(list) => {
            const modifiedList = list?.map((element) => {
              return { color_code: element?.additionFields?.color_code, ...element };
            });

            onChange(modifiedList);
          }}
          noFormInput={true}
          multiSelect={true}
        />
      </span>
    );
  } else if (type === "person" || type === "multi_person") {
    return (
      <span>
        <LabelComponent htmlFor={id || name} labelText={name} size="small" />
        <SelectComponent
          id={id || name}
          label={name}
          options={options}
          value={inputTypeData || []}
          onChange={(list) => {
            setInputTypeData(list);
            onChange(list);
          }}
          personList
          multiSelect={type === "multi_person"}
          noFormInput={true}
          showSearch={true}
        />
      </span>
    );
  } else {
    return (
      <span>
        <LabelComponent htmlFor={id || name} labelText={name} size="small" />
        <InputComponent
          id={id || name}
          type="text"
          placeholder={placeholder || `Enter ${name}`}
          value={value}
          onChange={(event) => onChange(event?.target?.value)}
        />
      </span>
    );
  }
};

const TableListRowAddEditComponent = ({ mode = "add", open, onClose, onSubmit = () => {}, prefilledFormData = [] }) => {
  const [toggleValue, setToggleValue] = useState("full");
  const [isLengthGreater, setIsLengthGreater] = useState(prefilledFormData?.length > 10 ? true : false);
  const [formInputData, setFormInputData] = useState(prefilledFormData);

  // Group form input data by sectionName (keep all items in state; we'll filter only when rendering)
  const groupedFormData = formInputData.reduce((acc, item) => {
    const section = item.sectionName || "General";
    if (!acc[section]) acc[section] = [];
    acc[section].push(item);
    return acc;
  }, {});

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b-[1px] border-[#D2DAE1]">
        <div className="flex items-center">
          <EditPencil className="mr-2" />
          <span className="text-base font-700 font-Inter text-16 text-[#004EFF]">
            {mode === "add" ? "Add" : "Edit"} Items
          </span>
        </div>
        <div className="flex gap-4 items-center">
          <button
            onClick={onClose}
            type="button"
            className="text-[#D2DAE1] w-7 h-7 ms-auto flex justify-center items-center hover:bg-[#F3F5F7] transition-colors duration-200 rounded-md"
            data-modal-hide="assets-modal"
          >
            <CrossCancelIcon className="h-3 w-3" />
          </button>
        </div>
      </div>

      {/* Form */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(formInputData);
        }}
        className="p-3 overflow-y-auto"
      >
        {Object.entries(groupedFormData).map(([sectionName, fields]) => (
          <div key={sectionName} className="mb-4">
            <div className="text-[#131313] font-semibold mb-2 font-Inter text-14">{getTitleCase(sectionName)}</div>

            <div className={`grid ${isLengthGreater ? "grid-cols-2 gap-3" : "grid-cols-1 gap-2"}`}>
              {/* Only render inputs where is_editable is NOT explicitly false */}
              {fields
                ?.filter((inputData) => inputData?.is_editable !== false)
                .map((inputData) => (
                  <div key={inputData?.name}>
                    {CheckTypeAndReturnHtmlInput({
                      id: inputData?.id,
                      name: inputData?.name,
                      type: inputData?.type,
                      placeholder: inputData?.placeholder,
                      value: inputData?.value,
                      options: inputData?.options,
                      metaType: inputData?.columnMetaType,
                      onChange: (value) => {
                        setFormInputData((prev) =>
                          prev.map((item) => (item?.name === inputData?.name ? { ...item, value } : item))
                        );
                      },
                    })}
                  </div>
                ))}
            </div>
          </div>
        ))}
      </form>

      {/* Footer */}
      <div className="p-3 col-span-full flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        <Button onClick={() => onSubmit(formInputData)} type="submit" variant="primary">
          Save
        </Button>
      </div>
    </Dialog>
  );
};

export default TableListRowAddEditComponent;
